/**
 * @module Analytics
 * @description The theme's single dataLayer writer. Listens for theme events and interactions
 * on tagged elements, and translates them into GA4 events for GTM to pick up.
 */
import { ThemeEvents } from '@theme/events';

window.dataLayer = window.dataLayer || [];

const DEBUG = new URLSearchParams(location.search).has('analytics_debug');

// Markup this file queries.
const SELECTORS = {
  trackedElement: '[data-analytics-track]',
  productItem: '[data-product-item]',     // product card
  productTitle: '[data-product-title]',   // cart form line, for cross-sell adds
  searchTerm: '[data-search-term]',       // predictive search results container
  crossSellSource: 'input[name="properties[_cross_sell_source]"]',
  addonOption: '[data-product-addon-option]:checked', // colour picked on an add-on row
  filterGroup: '[data-filter-name]',      // one facet in the collection filter
  // Form controls whose change is tracked. Price inputs are deliberately absent.
  // An add-on toggle only counts when it becomes ticked, hence the :checked.
  changeControls: '[data-filter-input], [data-filter-sort], [data-search-type], [data-product-addon-toggle]:checked, [data-product-addon-option]',
};

// Attributes this file reads off those elements, via getAttribute.
const ATTRIBUTES = {
  analyticsTrack: 'data-analytics-track',   // what the element is tracked as
  analyticsLabel: 'data-analytics-label',   // value the DOM can't supply (slider title, form placement)
  productTitle: 'data-product-title',
  searchTerm: 'data-search-term',
  filterName: 'data-filter-name',
  addonOption: 'data-product-addon-option', // colour picked on an add-on row
};

/* ---------------------------------------------------------------------------
 * GA4 event catalog
 *
 * The dataLayer contract with GTM: every event this module can push, with the
 * parameters it sets. Add new events and parameters here first; GTM tags and 
 * triggers mirror this list.
 * ------------------------------------------------------------------------- */

const GA4_EVENTS_AND_PARAMS = {
  // Search
  search_autocomplete: ['search_term'],
  search_autocomplete_type_change: ['search_term', 'search_type'],
  search_autocomplete_query_click: ['search_term', 'link_text', 'link_url'],
  search_autocomplete_view_all_click: ['search_term', 'link_text', 'link_url'],
  search_autocomplete_product_click: ['search_term', 'link_text', 'link_url'],
  search_autocomplete_article_click: ['search_term', 'link_text', 'link_url'],
  search_bar_focus: [],

  // Navigation
  mobile_nav_open: [],
  mobile_nav_click: ['link_text'],
  mobile_subnav_open: ['subnav_title'],
  desktop_nav_click: ['link_text'],
  desktop_subnav_open: ['subnav_title'],
  footer_nav_click: ['link_text'],
  promobar_click: ['link_text'],

  // Merchandising
  banner_click: ['link_text', 'link_url'],
  product_slider_click: ['slider_title', 'product_title'],
  complete_setup_click: ['product_title'],
  complete_setup_add: ['product_title'],
  related_item_click: ['product_title'],
  related_item_add: ['product_title'],
  cart_cross_sell_add: ['product_title'],
  addon_click: ['product_title'],
  addon_option_select: ['product_title', 'product_option'],
  addon_add: ['product_title', 'product_option'],

  // Product page
  product_gallery_nav: ['gallery_nav_type', 'gallery_image'],
  product_option_select: ['product_option'],
  modal_confidence: ['confidence_label'],

  // Collection
  filter_option_select: ['filter_type', 'filter_option'],
  sort_select: ['sort_option'],
  mobile_filter_open: [],

  // Age gate and forms
  modal_age_gate_view: [],
  modal_age_gate: ['link_text'],
  email_signup: ['signup_form_placement', 'signup_form_list_id'],
};

if (DEBUG) console.info('[analytics] event catalog', GA4_EVENTS_AND_PARAMS);

/** Every parameter key in the catalog — the schema pushed with each event. */
const UNIQUE_GA4_PARAMS = [...new Set(Object.values(GA4_EVENTS_AND_PARAMS).flat())];

/**
 * Pushes a GA4 event to the dataLayer with the full parameter schema.
 * @param {string} event - A GA4 event name from `GA4_EVENTS_AND_PARAMS`
 * @param {Object} [params] - Event parameters; only the event's declared keys are sent
 */
function track(event, params = {}) {
  const declared = GA4_EVENTS_AND_PARAMS[event];
  if (!declared) {
    console.warn('[analytics] event not in catalog, not pushed:', event, params);
    return;
  }

  // We force undefined on every unused param, since GTM merges previous data.
  // See https://www.simoahava.com/gtm-tips/remember-to-flush-unused-data-layer-variables/
  const payload = { event };
  for (const key of UNIQUE_GA4_PARAMS) payload[key] = declared.includes(key) ? params[key] ?? undefined : undefined;

  window.dataLayer.push(payload);
  if (DEBUG) console.info('[analytics]', payload);
}

/* ---------------------------------------------------------------------------
 * Theme event based trackers
 *
 * Components dispatch what happened in UI vocabulary (`@theme/events`); this
 * table translates it into GA4 events. Several entries may listen to the same
 * theme event, each guarding for its own case. Each entry:
 *   type         — the theme event listened for
 *   ga4Event     — GA4 event name from the catalog
 *   shouldTrack  — optional guard on `event.detail`
 *   getGa4Params — optional builder of GA4 parameters from `event.detail`
 * ------------------------------------------------------------------------- */

const THEME_EVENT_TRACKERS = [
  {
    type: ThemeEvents.resultsUpdated,
    ga4Event: 'search_autocomplete',
    // Queries shorter than 3 characters are filtered out
    shouldTrack: (detail) => (detail.results?.predictiveSearch?.query?.text?.length ?? 0) >= 3,
    getGa4Params: (detail) => ({ search_term: detail.results?.predictiveSearch?.query?.text }),
  },

  // Cross-sell adds, told apart by their `_cross_sell_source` line item property.
  {
    type: ThemeEvents.cartFormSubmitted,
    ga4Event: 'complete_setup_add',
    shouldTrack: ({ formElement }) => formElement?.querySelector(SELECTORS.crossSellSource)?.value === 'complete-setup',
    getGa4Params: ({ formElement }) => ({
      product_title: formElement.querySelector(SELECTORS.productTitle)?.getAttribute(ATTRIBUTES.productTitle) ?? '',
    }),
  },

  {
    type: ThemeEvents.cartFormSubmitted,
    ga4Event: 'related_item_add',
    shouldTrack: ({ formElement }) => formElement?.querySelector(SELECTORS.crossSellSource)?.value === 'related-items',
    getGa4Params: ({ formElement }) => ({
      product_title: formElement.querySelector(SELECTORS.productTitle)?.getAttribute(ATTRIBUTES.productTitle) ?? '',
    }),
  },

  {
    type: ThemeEvents.cartFormSubmitted,
    ga4Event: 'cart_cross_sell_add',
    shouldTrack: ({ formElement }) => formElement?.querySelector(SELECTORS.crossSellSource)?.value === 'cart',
    getGa4Params: ({ formElement }) => ({
      product_title: formElement.querySelector(SELECTORS.productTitle)?.getAttribute(ATTRIBUTES.productTitle) ?? '',
    }),
  },

  {
    type: ThemeEvents.sliderChanged,
    ga4Event: 'product_gallery_nav',
    // PDP gallery only. and only with a known interaction — swipes and programmatic
    // scrolls (e.g. variant-change auto-scroll) carry no direction and are ignored.
    shouldTrack: ({ id, direction }) => id === 'slider-product-gallery' && Boolean(direction),
    getGa4Params: ({ id, position, direction }) => {
      const gallery = window.POV.Slider?.find((slider) => slider.id === id);
      return {
        gallery_nav_type: direction, // 'next' | 'prev' | 'thumbnail'
        gallery_image: gallery?.itemAtPosition(position)?.querySelector('img')?.alt ?? '',
      };
    },
  },

  {
    type: ThemeEvents.optionSelected,
    ga4Event: 'product_option_select',
    // Only user-changed inputs carry an option, variant updates don't fire.
    shouldTrack: ({ option }) => Boolean(option),
    getGa4Params: ({ option }) => ({ product_option: option.value ?? '' }),
  },

  // filter.js only dispatches this below the L breakpoint, where the drawer is
  // user-opened rather than always-open.
  { type: ThemeEvents.filterOpened, ga4Event: 'mobile_filter_open' },

  { type: ThemeEvents.ageGateOpened, ga4Event: 'modal_age_gate_view' },

  {
    type: ThemeEvents.ageGateAnswered,
    ga4Event: 'modal_age_gate',
    getGa4Params: ({ answer }) => ({ link_text: answer ?? '' }),
  },
];

THEME_EVENT_TRACKERS.forEach(({ type, ga4Event, shouldTrack, getGa4Params }) => {
  document.addEventListener(type, (event) => {
    const detail = event.detail ?? {};
    if (shouldTrack && !shouldTrack(detail)) return;

    track(ga4Event, getGa4Params?.(detail));
  });
});

/* ---------------------------------------------------------------------------
 * Data attribute based trackers
 *
 * Document-delegated listeners for composed/generic markup and client-injected
 * content, so nothing needs component JS and no GA4 name leaves this file.
 * Markup only opts an element in with data-analytics-track (a `trackKey` below)
 * and, when the DOM can't supply a value, names it with data-analytics-label; see
 * SELECTORS and ATTRIBUTES at the top for every markup contract this file reads.
 * Per native DOM event `type`, `getTarget` resolves the interacted element from the
 * event (falsy skips) and each tracker says which catalog event its `trackKey` emits
 * and how its parameters are read: `getGa4Params(targetElement, trackedElement)`. The nearest tracked
 * ancestor with a tracker for the interaction wins, so a link deep inside the
 * mobile nav panel (toggle-only) still resolves to the drawer's click event.
 * ------------------------------------------------------------------------- */

const NATIVE_EVENT_TRACKERS = [
  {
    type: 'click',
    // Filter out clicks on buttons nested inside links (e.g. quick-buy in a product card)
    getTarget: (event) => !event.target.closest('button') && event.target.closest('a'),
    trackers: [
      {
        ga4Event: 'mobile_nav_click',
        trackKey: 'mobile-nav',
        getGa4Params: (link) => ({ link_text: link.textContent.trim() }),
      },
      {
        ga4Event: 'desktop_nav_click',
        trackKey: 'desktop-nav',
        getGa4Params: (link) => ({ link_text: link.textContent.trim() }),
      },
      {
        ga4Event: 'footer_nav_click',
        trackKey: 'footer-nav',
        getGa4Params: (link) => ({ link_text: link.textContent.trim() }),
      },
      {
        ga4Event: 'promobar_click',
        trackKey: 'promobar',
        getGa4Params: (link) => ({ link_text: link.textContent.trim() }),
      },
      {
        ga4Event: 'banner_click',
        trackKey: 'banner',
        getGa4Params: (link) => ({ link_text: link.textContent.trim(), link_url: link.href }),
      },
      {
        ga4Event: 'product_slider_click',
        trackKey: 'product-slider',
        getGa4Params: (link, slider) => ({
          slider_title: slider.getAttribute(ATTRIBUTES.analyticsLabel),
          product_title: link.closest(SELECTORS.productItem)?.getAttribute(ATTRIBUTES.productTitle),
        }),
      },
      {
        ga4Event: 'complete_setup_click',
        trackKey: 'complete-setup',
        getGa4Params: (link) => ({ product_title: link.closest(SELECTORS.productItem)?.getAttribute(ATTRIBUTES.productTitle) }),
      },
      {
        ga4Event: 'related_item_click',
        trackKey: 'related-items',
        getGa4Params: (link) => ({ product_title: link.closest(SELECTORS.productItem)?.getAttribute(ATTRIBUTES.productTitle) }),
      },
      {
        ga4Event: 'addon_click',
        trackKey: 'addons',
        getGa4Params: (link) => ({ product_title: link.closest(SELECTORS.productItem)?.getAttribute(ATTRIBUTES.productTitle) }),
      },
      // Predictive search results. predictive-search.js stores the query that
      // produced them as data-search-term on the results container.
      {
        ga4Event: 'search_autocomplete_query_click',
        trackKey: 'search-query',
        getGa4Params: (link, results) => ({
          search_term: results.closest(SELECTORS.searchTerm)?.getAttribute(ATTRIBUTES.searchTerm),
          link_text: link.textContent.trim(),
          link_url: link.href,
        }),
      },
      {
        ga4Event: 'search_autocomplete_view_all_click',
        trackKey: 'search-view-all',
        getGa4Params: (link, results) => ({
          search_term: results.closest(SELECTORS.searchTerm)?.getAttribute(ATTRIBUTES.searchTerm),
          link_text: link.textContent.trim(),
          link_url: link.href,
        }),
      },
      {
        ga4Event: 'search_autocomplete_product_click',
        trackKey: 'search-product',
        getGa4Params: (link, results) => ({
          search_term: results.closest(SELECTORS.searchTerm)?.getAttribute(ATTRIBUTES.searchTerm),
          link_text: link.closest(SELECTORS.productItem)?.getAttribute(ATTRIBUTES.productTitle),
          link_url: link.href,
        }),
      },
      {
        ga4Event: 'search_autocomplete_article_click',
        trackKey: 'search-article',
        getGa4Params: (link, results) => ({
          search_term: results.closest(SELECTORS.searchTerm)?.getAttribute(ATTRIBUTES.searchTerm),
          link_text: link.textContent.trim(),
          link_url: link.href,
        }),
      },
    ],
  },

  {
    type: 'toggle',
    // toggle doesn't bubble; capture phase reaches it. 
    capture: true,
    // <details> and <dialog> both fire toggle on open and close; only opens are tracked.
    getTarget: (event) =>
      (event.target instanceof HTMLDetailsElement || event.target instanceof HTMLDialogElement) &&
      event.target.open &&
      event.target,
    trackers: [
      {
        ga4Event: 'mobile_nav_open',
        trackKey: 'mobile-nav',
      },
      {
        ga4Event: 'mobile_subnav_open',
        trackKey: 'mobile-nav-panel',
        getGa4Params: (details) => ({ subnav_title: details.querySelector(':scope > summary')?.textContent.trim() }),
      },
      {
        ga4Event: 'desktop_subnav_open',
        trackKey: 'desktop-nav',
        getGa4Params: (details) => ({ subnav_title: details.querySelector(':scope > summary')?.textContent.trim() }),
      },
      {
        ga4Event: 'modal_confidence',
        trackKey: 'confidence',
        // dialog.liquid opens the <dialog> from the <button> immediately before it
        getGa4Params: (dialog) => ({ confidence_label: dialog.previousElementSibling?.textContent.trim() ?? '' }),
      },
    ],
  },

  {
    type: 'focus',
    // focus doesn't bubble; capture phase reaches it.
    capture: true,
    getTarget: (event) => event.target instanceof Element && event.target,
    trackers: [
      {
        ga4Event: 'search_bar_focus',
        trackKey: 'search-bar',
      },
    ],
  },

  {
    type: 'change',
    // change bubbles to document, so this one listener keeps working after filter.js swaps in fresh filter markup.
    getTarget: (event) => event.target instanceof Element && event.target.matches(SELECTORS.changeControls) && event.target,
    trackers: [
      {
        ga4Event: 'filter_option_select',
        trackKey: 'collection-filter',
        getGa4Params: (input) => ({
          filter_type: input.closest(SELECTORS.filterGroup)?.getAttribute(ATTRIBUTES.filterName) ?? '',
          filter_option: input.value ?? '',
        }),
      },
      {
        ga4Event: 'sort_select',
        trackKey: 'collection-sort',
        getGa4Params: (select) => ({ sort_option: select.selectedOptions[0]?.textContent.trim() ?? '' }),
      },
      {
        ga4Event: 'search_autocomplete_type_change',
        trackKey: 'search-type',
        getGa4Params: (radio) => ({
          search_term: radio.closest(SELECTORS.searchTerm)?.getAttribute(ATTRIBUTES.searchTerm) ?? '',
          search_type: radio.value ?? '',
        }),
      },
      {
        ga4Event: 'addon_option_select',
        trackKey: 'addon-option',
        getGa4Params: (radio) => ({
          product_title: radio.closest(SELECTORS.productItem)?.getAttribute(ATTRIBUTES.productTitle) ?? '',
          product_option: radio.getAttribute(ATTRIBUTES.addonOption) ?? '',
        }),
      },
      {
        ga4Event: 'addon_add',
        trackKey: 'addon-toggle',
        getGa4Params: (checkbox) => {
          const row = checkbox.closest(SELECTORS.productItem);
          return {
            product_title: row?.getAttribute(ATTRIBUTES.productTitle) ?? '',
            product_option: row?.querySelector(SELECTORS.addonOption)?.getAttribute(ATTRIBUTES.addonOption) ?? '',
          };
        },
      },
    ],
  },

  {
    type: 'submit',
    // The signup form is a plain <form> POST to Klaviyo's domain: the browser navigates away and the
    // theme never learns whether it succeeded, so the submit itself is what gets counted. Capture
    // runs this before any other submit handler could stop propagation.
    capture: true,
    getTarget: (event) => event.target instanceof HTMLFormElement && event.target,
    trackers: [
      {
        ga4Event: 'email_signup',
        trackKey: 'email-signup',
        getGa4Params: (form) => ({
          signup_form_placement: form.getAttribute(ATTRIBUTES.analyticsLabel),
          signup_form_list_id: new URL(form.getAttribute('action') ?? '', location.href).searchParams.get('g') ?? '',
        }),
      },
    ],
  },
];

NATIVE_EVENT_TRACKERS.forEach(({ type, capture = false, getTarget, trackers }) => {
  // Build a single selector that matches every trackKey in this event type's trackers.
  // E.g. for type: 'click': [data-analytics-track="mobile-nav"], [data-analytics-track="desktop-nav"], ...
  const trackKeysSelector = trackers.map(({ trackKey }) => `[${ATTRIBUTES.analyticsTrack}="${trackKey}"]`).join(', ');

  document.addEventListener(type, (event) => {
    const targetElement = getTarget(event);
    if (!targetElement) return;

    // With trackKeysSelector, closest() ignores ancestors without trackers for this event type.
    // E.g. a click inside "mobile-nav-panel" (only a 'toggle' tracker), is still only 
    // tracked as a "mobile-nav" 'click', since "mobile-nav" is the nearest ancestor 
    // with a 'click' tracker.
    const trackedElement = targetElement.closest(trackKeysSelector);
    if (!trackedElement) return;

    const trackKey = trackedElement.getAttribute(ATTRIBUTES.analyticsTrack);
    const tracker = trackers.find((candidate) => candidate.trackKey === trackKey);
    track(tracker.ga4Event, tracker.getGa4Params?.(targetElement, trackedElement));
  }, capture);
});

// A mistyped data-analytics-track value in markup would otherwise fail silently.
if (DEBUG) {
  const knownTrackKeys = NATIVE_EVENT_TRACKERS.flatMap(({ trackers }) => trackers.map(({ trackKey }) => trackKey));
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTORS.trackedElement).forEach((trackedElement) => {
      const trackKey = trackedElement.getAttribute(ATTRIBUTES.analyticsTrack);
      if (!knownTrackKeys.includes(trackKey)) console.warn('[analytics] unknown data-analytics-track value:', trackKey, trackedElement);
    });
  });
}
